import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import NotificationBell from "./NotificationBell";
import LanguageSwitcher from "./LanguageSwitcher";
import SubscriptionWarningPopup from "./SubscriptionWarningPopup";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Lightning,
  SquaresFour,
  Circuitry,
  Users,
  ChartLineUp,
  SignOut,
  Wallet,
  List,
  X,
  CaretDown,
  CurrencyCircleDollar,
  Buildings,
  ClockCounterClockwise,
  ShieldWarning,
  Heartbeat,
  FileText,
  GearSix,
  DownloadSimple,
  Package,
  Receipt,
  UserCircle,
  Trash,
  Queue,
  HouseSimple,
} from "@phosphor-icons/react";

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  const isTenant = user?.role === "tenant";

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      const dismissed = localStorage.getItem("pwa-install-dismissed");
      if (!dismissed) setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem("pwa-install-dismissed", "1");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getNavItems = () => {
    if (user?.role === "admin") {
      return [
        { path: "/admin", labelKey: "sidebar.dashboard", icon: SquaresFour },
        { path: "/admin/customers", labelKey: "sidebar.customers", icon: Users },
        { path: "/properties", labelKey: "sidebar.properties", icon: HouseSimple },
        { path: "/apartments", labelKey: "sidebar.apartments", icon: Buildings },
        { path: "/devices", labelKey: "sidebar.devices", icon: Circuitry },
        { path: "/tenants", labelKey: "sidebar.tenants", icon: Users },
        { path: "/admin/tariffs", labelKey: "sidebar.tariffs", icon: ChartLineUp },
        { path: "/admin/subscriptions", labelKey: "sidebar.subscription", icon: Wallet },
        { path: "/admin/finance", labelKey: "sidebar.invoices", icon: CurrencyCircleDollar },
        { path: "/alerts", labelKey: "sidebar.alerts", icon: ShieldWarning },
        { path: "/health", labelKey: "sidebar.health", icon: Heartbeat },
        { path: "/admin/activity", labelKey: "sidebar.activity", icon: ClockCounterClockwise },
        { path: "/admin/permission-templates", labelKey: "staff.permissions", icon: ShieldWarning },
        { path: "/admin/queue", labelKey: "sidebar.queue", icon: Queue },
        { path: "/deleted-invoices", labelKey: "sidebar.deleted_invoices", icon: Trash },
      ];
    }
    if (user?.role === "customer") {
      return [
        { path: "/dashboard", labelKey: "sidebar.dashboard", icon: SquaresFour },
        { path: "/properties", labelKey: "sidebar.properties", icon: HouseSimple },
        { path: "/apartments", labelKey: "sidebar.apartments", icon: Buildings },
        { path: "/devices", labelKey: "sidebar.devices", icon: Circuitry },
        { path: "/tenants", labelKey: "sidebar.tenants", icon: Users },
        { path: "/tariffs", labelKey: "sidebar.tariffs", icon: CurrencyCircleDollar },
        { path: "/fixed-expenses", labelKey: "sidebar.fixed_expenses", icon: Receipt },
        { path: "/invoices", labelKey: "sidebar.invoices", icon: FileText },
        { path: "/subscription", labelKey: "sidebar.subscription", icon: Package },
        { path: "/staff", labelKey: "sidebar.staff", icon: UserCircle },
        { path: "/activity", labelKey: "sidebar.activity", icon: ClockCounterClockwise },
        { path: "/alerts", labelKey: "sidebar.alerts", icon: ShieldWarning },
        { path: "/health", labelKey: "sidebar.health", icon: Heartbeat },
        { path: "/reports", labelKey: "sidebar.reports", icon: ChartLineUp },
        { path: "/cash-handover", labelKey: "sidebar.cash_handover", icon: Wallet },
        { path: "/deleted-invoices", labelKey: "sidebar.deleted_invoices", icon: Trash },
        { path: "/settings", labelKey: "sidebar.settings", icon: GearSix },
      ];
    }
    if (user?.role === "staff") {
      const p = user.permissions || {};
      const items = [
        { path: "/dashboard", labelKey: "sidebar.dashboard", icon: SquaresFour },
        { path: "/apartments", labelKey: "sidebar.apartments", icon: Buildings },
        { path: "/devices", labelKey: "sidebar.devices", icon: Circuitry },
      ];
      if (p.tenant_add || p.tenant_edit || p.tenant_remove || p.topup_load) {
        items.push({ path: "/tenants", labelKey: "sidebar.tenants", icon: Users });
      }
      if (p.tariff_set) {
        items.push({ path: "/tariffs", labelKey: "sidebar.tariffs", icon: CurrencyCircleDollar });
      }
      if (p.fixed_expense_add || p.fixed_expense_edit) {
        items.push({ path: "/fixed-expenses", labelKey: "sidebar.fixed_expenses", icon: Receipt });
      }
      if (p.reports_view) {
        items.push({ path: "/reports", labelKey: "sidebar.reports", icon: ChartLineUp });
      }
      items.push({ path: "/cash-handover", labelKey: "sidebar.cash_handover", icon: Wallet });
      return items;
    }
    return [
      { path: "/tenant", labelKey: "sidebar.dashboard", icon: SquaresFour },
      { path: "/topup", labelKey: "sidebar.topup", icon: Wallet },
      { path: "/tenant/invoices", labelKey: "sidebar.my_invoices", icon: Receipt },
      { path: "/reports", labelKey: "reports.consumption", icon: ChartLineUp },
    ];
  };

  const navItems = getNavItems();

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path || 
      (item.path === "/apartments" && location.pathname.includes("/apartments"));
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.path.replaceAll("/", "-").replace(/^-/, "")}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive
            ? "bg-indigo-50 text-indigo-600"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon size={22} weight={isActive ? "duotone" : "regular"} />
        {t(item.labelKey)}
      </Link>
    );
  };

  const getRoleLabel = (role) => {
    const roleMap = { admin: t("common.role_admin"), customer: t("common.role_customer"), tenant: t("common.role_tenant"), staff: t("common.role_staff") };
    return roleMap[role] || role;
  };

  const currentPageLabel = navItems.find((i) => i.path === location.pathname)?.labelKey;

  const isCustomerOrStaff = user?.role === "customer" || user?.role === "staff";
  const subscriptionBlocked = isCustomerOrStaff && user?.subscription_status && user.subscription_status !== "active";

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Subscription BLOCKING Overlay - abonelik yoksa veya suresi dolmussa */}
      {subscriptionBlocked && (
        <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subscription-block-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <ShieldWarning size={44} weight="duotone" className="text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {user.subscription_status === "expired" ? "Aboneliginiz Sona Erdi" : "Abonelik Tanimlanmamis"}
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                {user.subscription_status === "expired"
                  ? "Aboneliginizin suresi dolmustur. Sistemi kullanmaya devam etmek icin lutfen admin ile iletisime gecin ve aboneliginizi yenileyiniz."
                  : "Hesabiniza henuz bir abonelik plani tanimlanmamistir. Sistemi kullanabilmek icin lutfen admin ile iletisime gecin."}
              </p>
            </div>
            {user.subscription_end_date && user.subscription_status === "expired" && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-xs text-red-600">Bitis Tarihi: <span className="font-bold">{new Date(user.subscription_end_date).toLocaleDateString("tr-TR")}</span></p>
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-medium text-slate-600">Iletisim</p>
              <p className="text-sm text-slate-700">Sistem yoneticisi (admin) ile iletisime gecin ve abonelik odemesi hakkinda bilgi alin.</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full" data-testid="subscription-block-logout-btn">
              <SignOut size={18} className="mr-2" /> Cikis Yap
            </Button>
          </div>
        </div>
      )}

      {/* Subscription Warning Popup - customer/staff only */}
      {(user?.role === "customer" || user?.role === "staff") && !subscriptionBlocked && <SubscriptionWarningPopup />}

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r rtl:border-r-0 rtl:border-l border-slate-200 sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Lightning size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">NANOMET</h1>
              <p className="text-xs text-slate-500">Hub</p>
            </div>
          </Link>
        </div>

        <ScrollArea className="flex-1 p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-slate-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid="user-menu-trigger">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <span className="text-indigo-600 font-semibold">{user?.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="flex-1 text-left rtl:text-right">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500">{getRoleLabel(user?.role)}</p>
                </div>
                <CaretDown size={16} className="text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{t("common.name")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer">{t("settings.title")}</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="logout-btn">
                <SignOut size={18} className="mr-2" />
                {t("sidebar.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar - Mobile */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:hidden ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Lightning size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">NANOMET</h1>
              <p className="text-xs text-slate-500">Hub</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <ScrollArea className="flex-1 p-4 h-[calc(100vh-180px)]">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>
        </ScrollArea>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <span className="text-indigo-600 font-semibold">{user?.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div className="rtl:text-right">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="mobile-logout-btn">
            <SignOut size={18} className="mr-2" />
            {t("sidebar.logout")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-slate-100 rounded-lg lg:hidden" data-testid="mobile-menu-btn">
                <List size={24} className="text-slate-600" />
              </button>
              <div>
                <h2 className="font-heading text-lg sm:text-xl font-semibold text-slate-900">
                  {currentPageLabel ? t(currentPageLabel) : t("sidebar.dashboard")}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* PWA Install Banner */}
        {showInstallBanner && (
          <div className="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between gap-3" data-testid="pwa-install-banner">
            <div className="flex items-center gap-3 min-w-0">
              <DownloadSimple size={20} className="shrink-0" />
              <p className="text-sm truncate">NANOMET</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={handleInstall} className="bg-white text-indigo-600 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors" data-testid="pwa-install-btn">
                {t("common.download")}
              </button>
              <button onClick={dismissInstall} className="p-1 hover:bg-indigo-500 rounded-lg transition-colors" data-testid="pwa-dismiss-btn">
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        <main className={`flex-1 p-4 lg:p-8 ${isTenant ? "pb-24 lg:pb-8" : ""}`}>
          <div className="animate-fade-in">{children}</div>
        </main>

        {/* Bottom Tab Bar - Tenant Mobile Only */}
        {isTenant && (
          <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 lg:hidden safe-bottom" data-testid="bottom-tab-bar">
            <div className="flex items-center justify-around h-16">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                      isActive ? "text-indigo-600" : "text-slate-400"
                    }`}
                    data-testid={`tab-${item.path.replaceAll("/", "-").replace(/^-/, "")}`}
                  >
                    <Icon size={24} weight={isActive ? "fill" : "regular"} />
                    <span className="text-[11px] font-medium">{t(item.labelKey)}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
};

export default DashboardLayout;
